import { BackendApiClient } from './backend-client';
import type { ScopeContext } from '../model/types';
import { BackendValidationError } from '@/shared/lib/backend-errors';
import { requireList, UNREADABLE_RESPONSE_CODE } from '@/shared/lib/response-shape';
import type { UserStatus } from './user';

const backendClient = new BackendApiClient();

export interface IntegrationPublicKey {
  id: string;
  status: string;
  content: string;
  type?: string;
  reference_type?: string;
  reference_id?: string;
  owner_type?: string;
  owner_id?: string;
  expiration?: string;
  created?: string;
}

export interface Integration {
  id: string;
  user_id: string;
  name: string;
  status: UserStatus;
  allowed_ips?: string[];
  created?: string;
  public_key: IntegrationPublicKey | null;
}

export interface IntegrationResponse {
  integrations: Integration[];
  cursor?: string;
}

export interface IntegrationDetailResponse {
  integration: Integration;
}

export interface CreateIntegrationRequest {
  integrations: Array<{
    name: string;
    role_ids: string[];
    allowed_ips?: string[];
    public_key: string;
  }>;
}

export async function getIntegrations(
  sessionId: string,
  scopeContext: ScopeContext
): Promise<IntegrationResponse> {
  if (!scopeContext.scopeId || !scopeContext.scopeType) {
    throw new Error('Scope context must include scopeId and scopeType');
  }

  const response = await backendClient.get('/integration?status=active', sessionId, scopeContext);

  if (response.success && response.data) {
    // A cast is not a check: an `integrations` that is not an array survives `|| []` on the
    // page (an object is truthy) and throws inside `.map` during render, blanking the whole
    // console instead of showing a card. An empty array stays a legitimate empty state.
    const integrations = requireList<Integration>(
      response.data, 'integrations', 'Unreadable integration response',
    );
    const cursor = (response.data as { cursor?: unknown }).cursor;
    return { integrations, cursor: typeof cursor === 'string' ? cursor : '' };
  }

  const err = new Error(response.error || 'Failed to fetch integrations');
  if (response.errorCode) (err as { code?: string }).code = response.errorCode;
  throw err;
}

export async function getIntegration(
  sessionId: string,
  integrationId: string,
  scopeContext: ScopeContext
): Promise<IntegrationDetailResponse> {
  if (!scopeContext.scopeId || !scopeContext.scopeType) {
    throw new Error('Scope context must include scopeId and scopeType');
  }

  const response = await backendClient.get(
    `/integration/${integrationId}`,
    sessionId,
    scopeContext,
  );

  if (response.success && response.data) {
    const integration = (response.data as { integration?: unknown }).integration;
    if (!integration || typeof (integration as Integration).id !== 'string') {
      throw new BackendValidationError(
        'Unreadable integration response', UNREADABLE_RESPONSE_CODE,
      );
    }
    return { integration: integration as Integration };
  }

  throw new Error(response.error || 'Failed to fetch integration');
}

export async function createIntegration(
  sessionId: string,
  request: CreateIntegrationRequest,
  scopeContext: ScopeContext
): Promise<Integration> {
  if (!scopeContext.scopeId || !scopeContext.scopeType) {
    throw new Error('Scope context must include scopeId and scopeType');
  }

  const response = await backendClient.post('/integration', sessionId, request, scopeContext);

  if (!response.success) {
    throw response.validationError
      ?? new BackendValidationError(response.error || 'Failed to create integration');
  }

  const integrations = requireList<Integration>(
    response.data, 'integrations', 'Unreadable integration creation response',
  );
  const integration = integrations[0];
  if (!integration || typeof integration.id !== 'string' || !integration.id) {
    throw new BackendValidationError(
      'Integration creation returned no integration', UNREADABLE_RESPONSE_CODE,
    );
  }
  return integration;
}

export async function closeIntegration(
  sessionId: string,
  integrationId: string,
  scopeContext: ScopeContext
): Promise<void> {
  if (!scopeContext.scopeId || !scopeContext.scopeType) {
    throw new Error('Scope context must include scopeId and scopeType');
  }

  const body = { integration: { status: 'closed' } };

  const response = await backendClient.patch(
    `/integration/${integrationId}`,
    sessionId,
    body,
    scopeContext,
  );

  if (!response.success) {
    throw response.validationError
      ?? new BackendValidationError(response.error || 'Failed to close integration');
  }
}
